import { Card, Form, Switch } from "antd";
import * as React from "react";
import { FormattedMessage } from "react-intl";

import { Field } from "@components/field/field";
import { TranslationTabs } from "@components/translations/translationTabs/translationTabs";

import { languages } from "@pages/translationsPage/constants/languages";

import { EditorField } from "@components/field/editorField";
import { SwitchWrapper } from "@pages/addGalleriesPage/addGalleriesPageStyle";
import { LanguageErrors } from "@utils/errorsToLanguageErrors";
import { EventLanguageFieldProps } from "@utils/languageFieldProps";
import { maxCharacters } from "@utils/maxCharacters";
import { Editor_admin } from "@utils/roles";
import { LanguageObject } from "@utils/toLanguageObject";
import { useHasRole } from "@utils/useHasRole";
import { FormInstance } from "antd/lib/form";

export interface EventContentFormValues {
    title: LanguageObject;
    description: LanguageObject;
    withAdvancedMode: boolean;
}

export interface EventContentCardProps {
    form: FormInstance;
    hidden?: boolean;
    defaultValues: Partial<EventContentFormValues>;
    disabled?: boolean;
    activeLanguage: string;
    languageErrors?: LanguageErrors;
    isChronoRaceOnNormalAdmin: boolean;
    changeSelectedLanguage(languageCode: string): void;
    onBlur(): void;
    projectId: string;
    isSuperAdmin?: boolean;
}

const titleField = (props: EventLanguageFieldProps) => (
    <Field
        key={`title.${props.language}`}
        hidden={props.hidden || props.isChronoRaceOnNormalAdmin}
        id={`title.${props.language}`}
        info={maxCharacters()}
        label={<FormattedMessage id="title" />}
        name={["title", props.language]}
        rules={[
            {
                max: 140,
                message: <FormattedMessage id="form.toolong" />
            },
            {
                required: true,
                message: <FormattedMessage id="form.isrequired" />
            }
        ]}
        {...props.sharedProps}
    />
);

const descriptionField = (form: FormInstance) => (props: EventLanguageFieldProps) => (
    <Form.Item
        noStyle
        shouldUpdate={(pre, cur) => pre?.description?.[props.language] !== cur?.description?.[props.language]}
    >
        {() => {
            let descriptionFieldPath: (string|number)[] = ["description", props.language];
            const initialDescriptionObject = props.defaultValues.description;
            let projectDescriptionObject;
            let value: string | undefined;
            const indexOfProject = props.defaultValues?.projects?.findIndex(p => p.projectId === props.projectId);

            if (props.isChronoRaceOnNormalAdmin && indexOfProject !== undefined && indexOfProject !== -1) {
                descriptionFieldPath = ["projects", indexOfProject, "description", props.language];
                projectDescriptionObject = props.defaultValues?.projects?.[indexOfProject]?.description;
            }

            if (projectDescriptionObject && projectDescriptionObject[props.language]) {
                value = projectDescriptionObject[props.language];
            } else if (initialDescriptionObject && initialDescriptionObject[props.language]) {
                value = initialDescriptionObject[props.language];
            }

            return (
                <EditorField
                    key={`description.${props.language}`}
                    disabled={props.sharedProps.disabled}
                    form={props.form}
                    hasError={form.getFieldsError(descriptionFieldPath).find(val => Boolean(val.errors.length))}
                    hidden={props.hidden}
                    id={`description.${props.language}`}
                    isAdvancedMode={props.withAdvancedMode ?? false}
                    label={<FormattedMessage id="description" />}
                    name={descriptionFieldPath}
                    rules={
                        [{
                            required: true,
                            message: <FormattedMessage id="form.isrequired" />
                        }]
                    }
                    value={value}
                    {...props.sharedProps}
                />
            );
        }}
    </Form.Item>
);

export const EventContentCard: React.FC<EventContentCardProps> = React.memo(({
    changeSelectedLanguage,
    hidden,
    form,
    defaultValues,
    languageErrors,
    disabled,
    onBlur,
    activeLanguage,
    isChronoRaceOnNormalAdmin,
    projectId,
    isSuperAdmin
}) => {
    const sharedProps = {
        disabled,
        onBlur
    };

    const hasRole = useHasRole();
    const isEditorAdmin = hasRole(Editor_admin);
    const [withAdvancedMode, setWithAdvancedMode] = React.useState(form?.getFieldValue(["withAdvancedMode"]) ?? false);

    const langFieldProps = languages.map(language => ({
        isChronoRaceOnNormalAdmin,
        form,
        sharedProps,
        defaultValues,
        language: language.code,
        hidden: language.code !== activeLanguage,
        projectId
    }));

    return (
        <Card
            hidden={hidden}
            title={<FormattedMessage id="event" />}
        >
            <TranslationTabs
                activeLanguage={activeLanguage}
                errors={languageErrors}
                handleChangeSelectedLanguage={changeSelectedLanguage}
                languages={languages}
            />
            {langFieldProps.map(titleField)}
            {(isEditorAdmin || isSuperAdmin) && (
                <SwitchWrapper
                    direction="column"
                    margin="20px 0 0 0"
                >
                    <Form.Item
                        name="withAdvancedMode"
                        valuePropName="checked"
                    >
                        <Switch
                            {...sharedProps}
                            checkedChildren={<FormattedMessage id="editor.isAdvancedMode" />}
                            unCheckedChildren={<FormattedMessage id="editor.isBasicMode" />}
                            onChange={(value) => setWithAdvancedMode(value)}
                        />
                    </Form.Item>
                </SwitchWrapper>
            )}
            {langFieldProps.map((fieldProps) => descriptionField(form)({ ...fieldProps, withAdvancedMode }))}
        </Card>
    );
});
